// A3+ — вкладка «Проектное решение»: инверсия «Актуальности норм».
// Не «норма → где упоминается», а «решение → чем обосновано → живы ли обоснования».
// Карта строится автоматически в хвосте прогона «Проверить нормы» (LLM-аннотации
// только по проблемным нормам). «В замечания» — ОДНО замечание на решение.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, AlertTriangle, Send, ListChecks, FileSearch } from 'lucide-react';
import { api } from '@/api/client';
import { DocTraceSlideOver } from '@/components/DocTracePreview';
import { useMarkup } from '@/hooks/useMarkup';
import { MarkupToggle, MarkupFields } from '@/components/MarkupUI';

interface NormBrief {
  designation: string;
  status: string;
  status_title?: string | null;
  replaced_by?: string | null;
  verdict?: string | null;
}

interface QuoteBrief {
  document_title?: string | null;
  quote?: string | null;
  designation?: string | null;
  document_id?: string | null; // 15.A3: якорь трассируемости
}

interface Decision {
  id: string;
  section?: string | null;
  decision_title: string;
  decision_summary?: string | null;
  norms: NormBrief[];
  quotes?: QuoteBrief[] | null;
  listing_only: boolean;
  promoted_remark_id?: string | null;
}

const NORM_COLOR: Record<string, { color: string; bg: string }> = {
  cancelled: { color: '#B91C1C', bg: 'rgba(239,68,68,.10)' },
  replaced:  { color: '#B45309', bg: 'rgba(245,158,11,.12)' },
  not_found: { color: '#6D28D9', bg: 'rgba(124,58,237,.10)' },
};

export function DecisionImpactPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [traceQuote, setTraceQuote] = useState<QuoteBrief | null>(null); // 15.A3 slide-over
  const mk = useMarkup(projectId, 'decisions');  // 15.G разметка заказчика

  const { data } = useQuery<{ has_result: boolean; decisions: Decision[] }>({
    queryKey: ['norm-decisions', projectId],
    queryFn: () => api.get(`/projects/${projectId}/norms-actuality/decisions`),
    staleTime: 30_000,
  });

  const decisions = (data?.decisions ?? []).filter((d) => !d.listing_only);
  const listing = (data?.decisions ?? []).find((d) => d.listing_only);

  const promote = async (d: Decision) => {
    setBusy(d.id);
    try {
      await api.post(`/projects/${projectId}/norms-actuality/decision/${d.id}/promote`, {});
      qc.invalidateQueries({ queryKey: ['norm-decisions', projectId] });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
      <div className="flex items-start" style={{ gap: 12, marginBottom: 16 }}>
        <Landmark size={22} stroke="#0A1628" style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 260 }}>
          <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>
            Проектные решения и их обоснования
          </h4>
          <p className="text-muted" style={{ fontSize: 13, maxWidth: 680, lineHeight: 1.5 }}>
            Какие решения проекта опираются на заменённые или отменённые нормативы.
            Карта строится автоматически при проверке норм («Актуальность норм» →
            «Проверить нормы»). Одно решение — одно замечание проектировщику.
          </p>
        </div>
      </div>

      {!data?.has_result && (
        <div className="text-center text-muted" style={{ padding: '40px 20px', border: '1px dashed #E5E7EB', borderRadius: 12, fontSize: 13 }}>
          Карта решений ещё не построена. Запустите «Проверить нормы» на вкладке
          «Актуальность норм» — карта появится здесь после завершения прогона.
        </div>
      )}

      {data?.has_result && (
        <>
          <div className="flex items-center" style={{ gap: 10, marginBottom: 16, fontSize: 13 }}>
            <span style={{ padding: '4px 12px', borderRadius: 16, background: 'rgba(239,68,68,.08)', color: '#B91C1C', fontWeight: 600 }}>
              <AlertTriangle size={13} style={{ display: 'inline', verticalAlign: -2, marginRight: 5 }} />
              Затронуто решений: {decisions.length}
            </span>
            {listing && (
              <span className="text-muted" style={{ fontSize: 12 }}>
                + {listing.norms.length} норм только в перечнях НТД (решений не затрагивают)
              </span>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <MarkupToggle on={mk.markupMode} onToggle={() => mk.setMarkupMode((v) => !v)} savedAt={mk.savedAt} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {decisions.map((d) => {
              const isOpen = expanded === d.id;
              return (
                <div key={d.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : d.id)}
                    style={{ width: '100%', textAlign: 'left', padding: '12px 16px', background: '#FFF', border: 'none', cursor: 'pointer' }}
                  >
                    <div className="flex items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#0A1628' }}>{d.decision_title}</span>
                      {d.section && <span className="text-muted" style={{ fontSize: 12 }}>{d.section}</span>}
                      {d.promoted_remark_id && (
                        <span style={{ fontSize: 11, color: '#047857', marginLeft: 'auto' }}>✓ в замечаниях</span>
                      )}
                    </div>
                    <div className="flex items-center" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {d.norms.map((n) => {
                        const c = NORM_COLOR[n.status] || { color: '#6B7280', bg: 'rgba(107,114,128,.08)' };
                        return (
                          <span key={n.designation} className="rounded-pill" style={{
                            padding: '2px 10px', fontSize: 11, fontWeight: 600, background: c.bg, color: c.color,
                          }}>
                            {n.designation}{n.replaced_by ? ` → ${n.replaced_by}` : ''}
                          </span>
                        );
                      })}
                    </div>
                  </button>
                  {mk.markupMode && (
                    <div style={{ padding: '0 16px 12px' }}>
                      <MarkupFields value={mk.labels[d.id]} onChange={(p) => mk.setLabel(d.id, p)} />
                    </div>
                  )}
                  {isOpen && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', fontSize: 13 }}>
                      {d.decision_summary && (
                        <p style={{ marginBottom: 10, lineHeight: 1.55, color: '#374151' }}>{d.decision_summary}</p>
                      )}
                      {(d.quotes || []).slice(0, 6).map((q, i) => (
                        <div key={i} style={{ padding: '8px 12px', borderLeft: '3px solid #E5E7EB', background: '#FFF', borderRadius: 4, fontSize: 12, color: '#374151', marginBottom: 6, lineHeight: 1.5 }}>
                          <span className="text-muted" style={{ fontSize: 11 }}>{q.document_title} · {q.designation}: </span>
                          …{q.quote}…
                          {/* Каждое обоснование трассируется отдельно (они в разных местах ПД). */}
                          {q.document_id && q.quote && (
                            <div style={{ marginTop: 6 }}>
                              <button type="button" onClick={() => setTraceQuote(q)}
                                title="Открыть лист документа с подсветкой обоснования"
                                style={{
                                  padding: '5px 12px', borderRadius: 16, fontSize: 12,
                                  background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB',
                                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                                }}
                              >
                                <FileSearch size={12} /> Показать в документе
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {!d.promoted_remark_id && (
                        <button type="button" onClick={() => promote(d)} disabled={busy === d.id}
                          style={{
                            marginTop: 8, padding: '7px 16px', borderRadius: 18, fontSize: 12, fontWeight: 600,
                            background: '#0A1628', color: '#FFF', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6, opacity: busy === d.id ? 0.6 : 1,
                          }}
                        >
                          <Send size={12} /> {busy === d.id ? 'Создаю…' : 'В замечания (одно на решение)'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {listing && (
            <div style={{ marginTop: 18, border: '1px dashed #E5E7EB', borderRadius: 12, padding: '12px 16px' }}>
              <div className="flex items-center" style={{ gap: 8, marginBottom: 8 }}>
                <ListChecks size={15} stroke="#6B7280" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{listing.decision_title}</span>
                {!listing.promoted_remark_id ? (
                  <button type="button" onClick={() => promote(listing)} disabled={busy === listing.id}
                    style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 14, fontSize: 11, background: 'transparent', border: '1px solid #E5E7EB', color: '#6B7280', cursor: 'pointer' }}>
                    {busy === listing.id ? 'Создаю…' : 'В замечания (обновить перечень)'}
                  </button>
                ) : (
                  <span style={{ fontSize: 11, color: '#047857', marginLeft: 'auto' }}>✓ в замечаниях</span>
                )}
              </div>
              <div className="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                {listing.norms.map((n) => (
                  <span key={n.designation} className="text-muted rounded-pill" style={{ padding: '2px 8px', fontSize: 11, background: '#F3F4F6' }}>
                    {n.designation}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {traceQuote && (
        <DocTraceSlideOver
          title={traceQuote.designation || 'Обоснование решения'}
          subtitle={traceQuote.document_title || null}
          quote={traceQuote.quote || null}
          documentId={traceQuote.document_id}
          page={null}
          onClose={() => setTraceQuote(null)}
        />
      )}
    </div>
  );
}
